import { Injectable } from '@angular/core';
import { httpsCallable } from '@angular/fire/functions';
import {
  createFunctionResult,
  FUNCTION_RESULT_STATUS,
  FunctionResult,
  ICustomer,
  IObjectKey
} from '@nova/common-interfaces';
import { FUNCTION_NAME, ICustomerAccount, ICustomerListModel, ICustomerOverview, MailAttachment } from '@rfm/shared-interfaces';
import { BehaviorSubject } from 'rxjs';
import { CommonService } from '../../../../lib/services/index.mjs';

@Injectable({
  providedIn: 'root',
})
export class CustomerService extends CommonService<ICustomer, IObjectKey> {
  protected override deleteFunctionName: FUNCTION_NAME = FUNCTION_NAME.CUSTOMERS_CUSTOMER_REMOVE;
  protected override getAllFunctionName: FUNCTION_NAME = FUNCTION_NAME.CUSTOMERS_CUSTOMER_GETALL;
  protected override getFunctionName: FUNCTION_NAME = FUNCTION_NAME.CUSTOMERS_CUSTOMER_GET;
  protected override getKeysFunctionName: FUNCTION_NAME = FUNCTION_NAME.CUSTOMERS_CUSTOMER_GETKEYS;
  protected override insertFunctionName: FUNCTION_NAME = FUNCTION_NAME.CUSTOMERS_CUSTOMER_INSERT;
  protected override updateFunctionName: FUNCTION_NAME = FUNCTION_NAME.CUSTOMERS_CUSTOMER_UPDATE;
  protected override mergeFunctionName: FUNCTION_NAME = FUNCTION_NAME.CUSTOMERS_CUSTOMER_MERGE;
 
  protected getOverviewFunctionName: FUNCTION_NAME = FUNCTION_NAME.CUSTOMERS_CUSTOMER_GETOVERVIEW;
  protected getInvoiceableFunctionName: FUNCTION_NAME = FUNCTION_NAME.CUSTOMERS_CUSTOMER_GETINVOICEABLE;
  protected getAllAsListFunctionName: FUNCTION_NAME = FUNCTION_NAME.CUSTOMERS_CUSTOMER_GETALLASLIST;
  protected getAllWithBalanceFunctionName: FUNCTION_NAME = FUNCTION_NAME.CUSTOMERS_CUSTOMER_GETALLWITHBALANCE;
  protected suspendFunctionName: FUNCTION_NAME = FUNCTION_NAME.CUSTOMERS_CUSTOMER_SUSPEND;
  protected cancelFunctionName: FUNCTION_NAME = FUNCTION_NAME.CUSTOMERS_CUSTOMER_CANCEL;
  protected activateFunctionName: FUNCTION_NAME = FUNCTION_NAME.CUSTOMERS_CUSTOMER_ACTIVATE;
  protected getSendEmailFunctionName: FUNCTION_NAME = FUNCTION_NAME.CUSTOMERS_CUSTOMER_SENDEMAIL;
  protected refreshBalanceFunctionName: FUNCTION_NAME = FUNCTION_NAME.CUSTOMERS_CUSTOMER_REFRESHBALANCE;
  state: BehaviorSubject<ICustomerOverview> | undefined;
  
  public async getOverview(uid: string, forceRefresh: boolean = false): Promise<FunctionResult<ICustomerOverview>> {
    this.isLoading(true);

    if (this.state && this.state.value.id === uid && !forceRefresh)
    {
      return Promise.resolve(createFunctionResult<ICustomerOverview>(FUNCTION_RESULT_STATUS.SUCCESS, this.state.value));
    }

    console.log("Calling Overview service", uid)

    return new Promise<FunctionResult<ICustomerOverview>>((resolve, reject) => {
      const callable = httpsCallable<IObjectKey | string, FunctionResult<ICustomerOverview>>(
        this.functions,
        this.getOverviewFunctionName.toString()
      );
      callable(uid)
        .then(
          (result) => {
            this.state = new BehaviorSubject<ICustomerOverview>(result.data.data!);
            resolve(result.data);
          },
          (error) => {
            console.error(error);
            reject(createFunctionResult<ICustomerOverview | null>(FUNCTION_RESULT_STATUS.ERROR, null));
          }
        )
        .finally(() => {
          this.isLoading(false);
        });
    });
  }

  public async getAllWithBalance(): Promise<FunctionResult<ICustomerAccount[]>> {
    this.isLoading(true);

    return new Promise<FunctionResult<ICustomerAccount[]>>((resolve, reject) => {
      const callable = httpsCallable<IObjectKey | string, FunctionResult<ICustomerAccount[]>>(
        this.functions,
        this.getAllWithBalanceFunctionName.toString()
      );
      callable()
        .then(
          (result) => {
            console.debug("Result", result.data);
            resolve(result.data);
          },
          (error) => {
            console.error(error);
            reject(createFunctionResult<ICustomerAccount[] | null>(FUNCTION_RESULT_STATUS.ERROR, null));
          }
        )
        .finally(() => {
          this.isLoading(false);
        });
    });
  }

  public async getInvoiceable(): Promise<FunctionResult<ICustomer[]>> {
    this.isLoading(true);

    return new Promise<FunctionResult<ICustomer[]>>((resolve, reject) => {
      const callable = httpsCallable<IObjectKey | string, FunctionResult<ICustomer[]>>(
        this.functions,
        this.getInvoiceableFunctionName.toString()
      );
      callable()
        .then(
          (result) => {
            console.debug("Result", result.data);
            resolve(result.data);
          },
          (error) => {
            console.error(error);
            reject(createFunctionResult<ICustomer[] | null>(FUNCTION_RESULT_STATUS.ERROR, null));
          }
        )
        .finally(() => {
          this.isLoading(false);
        });
    });
  }

  public async sendEmail(
    toAddresses: string[],
    fromAddress: string,
    fromName: string,
    subject: string,
    text_body: string,
    html_body: string,
    attachment?: MailAttachment): Promise<FunctionResult<ICustomerOverview>> {
    this.isLoading(true);

    return new Promise<FunctionResult<ICustomerOverview>>((resolve, reject) => {
      const callable = httpsCallable<any, FunctionResult<ICustomerOverview>>(
        this.functions,
        this.getSendEmailFunctionName.toString()
      );
      callable({
        "toAddresses": toAddresses,
        "fromAddress": fromAddress,
        "fromName": fromName,
        "subject": subject,
        "text_body": text_body,
        "html_body": html_body,
        "attachment": attachment
      })
        .then(
          (result) => {
            console.debug("Result", result.data);
            resolve(result.data);
          },
          (error) => {
            console.error(error);
            reject(createFunctionResult<ICustomerOverview | null>(FUNCTION_RESULT_STATUS.ERROR, null));
          }
        )
        .finally(() => {
          this.isLoading(false);
        });
    });
  }

  public async getAllAsList(): Promise<FunctionResult<ICustomerListModel[]>> {
    this.isLoading(true);

    return new Promise<FunctionResult<ICustomerListModel[]>>((resolve, reject) => {
      const callable = httpsCallable<null, FunctionResult<ICustomerListModel[]>>(
        this.functions,
        this.getAllAsListFunctionName.toString()
      );
      callable()
        .then(
          (result) => {
            console.debug("Result", result.data);
            resolve(result.data);
          },
          (error) => {
            console.error(error);
            reject(createFunctionResult<ICustomerListModel[] | null>(FUNCTION_RESULT_STATUS.ERROR, null));
          }
        )
        .finally(() => {
          this.isLoading(false);
        });
    });
  }
  /**
   * Update object of type T
   * Allows for optional refreshing of the service's objects collection (Default: false)
   * @param { T } obj
   * @param { boolean} refresh
   * @return { Promise<T> }
   */
  public async suspend(key: IObjectKey | string): Promise<FunctionResult<ICustomer>> {
    this.isLoading(true);
    console.debug("Calling Function", this.suspendFunctionName.toString());

    return new Promise<FunctionResult<ICustomer>>((resolve, reject) => {
      const callable = httpsCallable<any, FunctionResult<ICustomer>>(
        this.functions,
        this.suspendFunctionName.toString()
      );
      callable(key)
        .then(
          (result) => {
            console.debug("Result", result.data);
            resolve(result.data);
          },
          (error) => {
            console.error(error);
            reject(error);
          }
        )
        .finally(() => {
          this.isLoading(false);
        });
    });
  }

  /**
   * Update object of type T
   * Allows for optional refreshing of the service's objects collection (Default: false)
   * @param { T } obj
   * @param { boolean} refresh
   * @return { Promise<T> }
   */
  public async refreshCustomerBalance(key: IObjectKey | string): Promise<FunctionResult<ICustomer>> {
    this.isLoading(true);
    console.debug("Calling Function", this.refreshBalanceFunctionName.toString());

    return new Promise<FunctionResult<ICustomer>>((resolve, reject) => {
      const callable = httpsCallable<any, FunctionResult<ICustomer>>(
        this.functions,
        this.refreshBalanceFunctionName.toString()
      );
      callable(key)
        .then(
          (result) => {
            console.debug("Result", result.data);
            resolve(result.data);
          },
          (error) => {
            console.error(error);
            reject(error);
          }
        )
        .finally(() => {
          this.isLoading(false);
        });
    });
  }

  /**
   * Update object of type T
   * Allows for optional refreshing of the service's objects collection (Default: false)
   * @param { T } obj
   * @param { boolean} refresh
   * @return { Promise<T> }
   */
  public async activate(key: IObjectKey | string): Promise<FunctionResult<ICustomer>> {
    this.isLoading(true);
    console.debug("Calling Function", this.activateFunctionName.toString());

    return new Promise<FunctionResult<ICustomer>>((resolve, reject) => {
      const callable = httpsCallable<any, FunctionResult<ICustomer>>(
        this.functions,
        this.activateFunctionName.toString()
      );
      callable(key)
        .then(
          (result) => {
            console.debug("Result", result.data);
            resolve(result.data);
          },
          (error) => {
            console.error(error);
            reject(error);
          }
        )
        .finally(() => {
          this.isLoading(false);
        });
    });
  }

  /**
   * Update object of type T
   * Allows for optional refreshing of the service's objects collection (Default: false)
   * @param { T } obj
   * @param { boolean} refresh
   * @return { Promise<T> }
   */
  public async cancel(key: IObjectKey | string): Promise<FunctionResult<ICustomer>> {
    this.isLoading(true);
    console.debug("Calling Function", this.cancelFunctionName.toString());

    return new Promise<FunctionResult<ICustomer>>((resolve, reject) => {
      const callable = httpsCallable<any, FunctionResult<ICustomer>>(
        this.functions,
        this.cancelFunctionName.toString()
      );
      callable(key)
        .then(
          (result) => {
            console.debug("Result", result.data);
            resolve(result.data);
          },
          (error) => {
            console.error(error);
            reject(error);
          }
        )
        .finally(() => {
          this.isLoading(false);
        });
    });
  }

}
